import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Divider,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Profile } from '@par/shared/types/player';

type ActiveSubTabs =
  | 'overview'
  | 'achievements'
  | 'punchcard'
  | 'advanced-stats'
  | 'custom-questions'
  | 'game-settings'
  | 'account';

type PropTypes = {
  profile: Profile;
  showAccountBoundMenus: boolean;
  viewProfileId: string;
};

export const ProfileSubNav = ({
  profile,
  showAccountBoundMenus,
  viewProfileId,
}: PropTypes) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const getActiveTab = () => {
    if (location.pathname.includes('/profile/overview')) return 'overview';
    if (location.pathname.includes('/profile/achievements'))
      return 'achievements';
    if (location.pathname.includes('/profile/punchcard')) return 'punchcard';
    if (location.pathname.includes('/profile/advanced-stats'))
      return 'advanced-stats';
    if (location.pathname.includes('/profile/custom-questions'))
      return 'custom-questions';
    if (location.pathname.includes('/profile/game-settings'))
      return 'game-settings';
    if (location.pathname.includes('/profile/account')) return 'account';
  };

  const [activeSubTab, setActiveSubTab] = useState<ActiveSubTabs>(getActiveTab);

  useEffect(() => {
    setActiveSubTab(getActiveTab);
  }, [location]);

  const SubHeaderTab = (isActiveTab: boolean, text: string) => (
    <Typography
      variant={isActiveTab ? 'subtitle2led' : 'subtitle2'}
      letterSpacing={2}
      color={!isActiveTab ? 'info.light' : null}
      sx={{
        filter: isActiveTab
          ? 'drop-shadow(0 0 15px #db3eb1) drop-shadow(0 0 50px #db3eb1) contrast(2)'
          : null,
      }}
    >
      {text}
    </Typography>
  );

  const handleOverviewClick = () =>
    navigate(`/profile/overview/${viewProfileId}`);
  const handleAchievementsClick = () =>
    navigate(`/profile/achievements/${viewProfileId}`);
  const handlePunchcardClick = () =>
    navigate(`/profile/punchcard/${viewProfileId}`);
  const handleAdvancedStatsClick = () =>
    navigate(`/profile/advanced-stats/${viewProfileId}`);
  const handleCustomQuestionsClick = () =>
    navigate(`/profile/custom-questions/${viewProfileId}`);
  const handleGameSettingsClick = () =>
    navigate(`/profile/game-settings/${viewProfileId}`);
  const handleAccountClick = () =>
    navigate(`/profile/account/${viewProfileId}`);

  const handleSubNavChange = (event: any, tab: ActiveSubTabs) => {
    setActiveSubTab(tab);
    if (tab === 'overview') handleOverviewClick();
    if (tab === 'achievements') handleAchievementsClick();
    if (tab === 'punchcard') handlePunchcardClick();
    if (tab === 'advanced-stats') handleAdvancedStatsClick();
    if (tab === 'custom-questions') handleCustomQuestionsClick();
    if (tab === 'game-settings') handleGameSettingsClick();
    if (tab === 'account') handleAccountClick();
  };

  return (
    <Container>
      <Box display={'flex'} justifyContent={'left'} mt={2}>
        <Tabs
          variant="scrollable"
          value={false}
          onChange={handleSubNavChange}
          indicatorColor="secondary"
          sx={{
            width: '100%',
            height: 0,
            borderRadius: '10px',
            bgcolor: '#190c1a',
            '& .MuiTabs-flexContainer': { height: '100%' },
          }}
        >
          <Tab
            label={SubHeaderTab(activeSubTab === 'overview', 'Overview')}
            value="overview"
          />
          <Divider
            flexItem
            orientation="vertical"
            sx={{
              height: '40%',
              alignSelf: 'center',
              borderRightWidth: 2,
            }}
          />
          <Tab
            label={SubHeaderTab(
              activeSubTab === 'achievements',
              'Achievements'
            )}
            value="achievements"
          />
          <Divider
            flexItem
            orientation="vertical"
            sx={{
              height: '40%',
              alignSelf: 'center',
              borderRightWidth: 2,
            }}
          />
          <Tab
            label={SubHeaderTab(activeSubTab === 'punchcard', 'Solo Punchcard')}
            value="punchcard"
          />
          <Divider
            flexItem
            orientation="vertical"
            sx={{
              height: '40%',
              alignSelf: 'center',
              borderRightWidth: 2,
            }}
          />
          <Tab
            label={SubHeaderTab(activeSubTab === 'advanced-stats', 'Adv Stats')}
            value="advanced-stats"
          />
          {showAccountBoundMenus ? (
            <Divider
              flexItem
              orientation="vertical"
              sx={{
                height: '40%',
                alignSelf: 'center',
                borderRightWidth: 2,
              }}
            />
          ) : null}
          {showAccountBoundMenus ? (
            <Tab
              label={SubHeaderTab(
                activeSubTab === 'custom-questions',
                'Custom Questions'
              )}
              value="custom-questions"
            />
          ) : null}
          {viewProfileId === profile.id ? (
            <Divider
              flexItem
              orientation="vertical"
              sx={{
                height: '40%',
                alignSelf: 'center',
                borderRightWidth: 2,
              }}
            />
          ) : null}
          {showAccountBoundMenus ? (
            <Tab
              label={SubHeaderTab(activeSubTab === 'game-settings', 'Settings')}
              value="game-settings"
            />
          ) : null}
          {showAccountBoundMenus ? (
            <Divider
              flexItem
              orientation="vertical"
              sx={{
                height: '40%',
                alignSelf: 'center',
                borderRightWidth: 2,
              }}
            />
          ) : null}
          {showAccountBoundMenus ? (
            <Tab
              label={SubHeaderTab(activeSubTab === 'account', 'Account')}
              value="account"
            />
          ) : null}
        </Tabs>
      </Box>
    </Container>
  );
};
